import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchInstitutionalDashboard, type InstitutionalDashboardData } from '../lib/queries';
import { PageHeader, ProgressBar, QueryState, UnitStatusPill } from '../components/ui';
import { AttentionTable, EvolutionLegend, Kpi, MonthlyEvolutionChart, RiskKpis } from '../components/DashboardWidgets';
import type { UnitRatStatus } from '@rat/shared';

type UnitRow = InstitutionalDashboardData['por_unidad'][number];
type SortKey = 'name' | 'rat_status' | 'activities_total' | 'completeness_avg';

export function InstitutionalDashboard() {
  const q = useQuery({ queryKey: ['dash', 'inst'], queryFn: fetchInstitutionalDashboard });
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'name', dir: 1 });

  const sortedUnits = useMemo(() => {
    const rows = q.data?.por_unidad ?? [];
    return [...rows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), 'es');
      return cmp * sort.dir;
    });
  }, [q.data?.por_unidad, sort]);

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  }

  function sortArrow(key: SortKey) {
    if (sort.key !== key) return null;
    return <span style={{ fontSize: 10, marginLeft: 3 }}>{sort.dir === 1 ? '▲' : '▼'}</span>;
  }

  return (
    <>
      <PageHeader
        eyebrow="Responsabilidad proactiva · Ley 21.719"
        title="Tablero institucional"
        sub="Avance del Registro de Actividades de Tratamiento"
        actions={
          <Link className="btn" to="/reportes">
            Exportar reporte
          </Link>
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <>
            <div className="kpis" style={{ marginBottom: 18 }}>
              <Kpi n={q.data.unidades_total} l="Unidades / direcciones" />
              <Kpi n={q.data.actividades_total} l="Actividades" />
              <Kpi
                n={`${q.data.completitud_institucional}%`}
                l="Completitud institucional"
                title="Promedio de completitud de TODAS las actividades (no el promedio de los promedios por unidad)"
              />
              <Kpi
                n={q.data.unidades_sin_actividades}
                l="Unidades sin actividades"
                tone={q.data.unidades_sin_actividades > 0 ? 'warning' : undefined}
                title="Unidades que todavía no registran ninguna actividad de tratamiento"
              />
              <Kpi n={q.data.por_estado['EN_REVISION'] ?? 0} l="En revisión" />
              <Kpi n={q.data.por_estado['APROBADO'] ?? 0} l="Aprobadas" />
              <Kpi n={q.data.por_estado['CERRADO'] ?? 0} l="Cerradas" />
            </div>

            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Riesgo y cumplimiento
            </div>
            <RiskKpis riesgo={q.data.riesgo} />

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 16, marginBottom: 16 }}>
              <div className="card">
                <div className="card__h">
                  <h3 style={{ fontSize: 15 }}>Necesitan atención</h3>
                </div>
                <div className="card__b">
                  <AttentionTable rows={q.data.atencion} showUnit />
                </div>
              </div>

              <div className="card">
                <div className="card__h">
                  <h3 style={{ fontSize: 15 }}>Evolución mensual</h3>
                </div>
                <div className="card__b">
                  <MonthlyEvolutionChart data={q.data.evolucion_mensual} />
                  <EvolutionLegend />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card__h">
                <h3 style={{ fontSize: 15 }}>Avance por unidad</h3>
              </div>
              <div className="card__b">
                <div className="tablewrap" style={{ border: 0 }}>
                  <table className="data">
                    <thead>
                      <tr>
                        <SortableTh label="Unidad" sortKey="name" onClick={toggleSort} arrow={sortArrow('name')} />
                        <SortableTh label="Estado" sortKey="rat_status" onClick={toggleSort} arrow={sortArrow('rat_status')} />
                        <SortableTh label="Actividades" sortKey="activities_total" onClick={toggleSort} arrow={sortArrow('activities_total')} />
                        <SortableTh label="Completitud" sortKey="completeness_avg" onClick={toggleSort} arrow={sortArrow('completeness_avg')} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUnits.map((u) => (
                        <tr key={u.unit_id}>
                          <td>
                            <Link to={`/unidad/${u.unit_id}`}>{u.name}</Link>
                          </td>
                          <td>
                            <UnitStatusPill status={u.rat_status as UnitRatStatus} />
                          </td>
                          <td className="mono">{u.activities_total}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <ProgressBar pct={u.completeness_avg} />
                              <span className="mono">{u.completeness_avg}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </QueryState>
    </>
  );
}

function SortableTh<K extends string>({
  label,
  sortKey,
  onClick,
  arrow,
}: {
  label: string;
  sortKey: K;
  onClick: (key: K) => void;
  arrow: ReactNode;
}) {
  return (
    <th
      onClick={() => onClick(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title={`Ordenar por ${label.toLowerCase()}`}
    >
      {label}
      {arrow}
    </th>
  );
}
